import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Button, cx } from './ui'

/**
 * Vote and favorite. Both are optimistic: the counter moves immediately and
 * rolls back if the write fails, because the round trip is the whole latency.
 */
export function DocActions({
  docId,
  votes: initialVotes,
  favorited: initialFavorited,
}: {
  docId: number
  votes: number
  favorited: boolean
}) {
  const { user, signIn } = useAuth()
  const [votes, setVotes] = useState(initialVotes)
  // `GET /docs/{id}` returns the net score and `favorited`, but not *this* user's
  // vote — so on load we cannot highlight an existing vote. It self-corrects on
  // the first click (the server toggles), and would be fixed by adding e.g.
  // `my_vote` to the doc payload.
  const [myVote, setMyVote] = useState<1 | -1 | 0>(0)
  const [favorited, setFavorited] = useState(initialFavorited)
  const [busy, setBusy] = useState(false)

  if (!user) {
    return (
      <div className="flex items-center gap-3 text-sm text-subtle">
        <span>{votes} votes</span>
        <button onClick={() => void signIn()} className="font-medium text-accent-text hover:underline">
          Sign in to vote, favorite, and comment
        </button>
      </div>
    )
  }

  const vote = async (value: 1 | -1) => {
    if (busy) return
    setBusy(true)

    const wasMine = myVote === value
    const before = { votes, myVote }
    // Re-sending the same value toggles it off, so the local delta has to undo
    // the previous vote as well as apply the new one.
    setVotes(votes - myVote + (wasMine ? 0 : value))
    setMyVote(wasMine ? 0 : value)

    try {
      setVotes(await api.vote(docId, value))
    } catch {
      setVotes(before.votes)
      setMyVote(before.myVote)
    } finally {
      setBusy(false)
    }
  }

  const toggleFavorite = async () => {
    if (busy) return
    setBusy(true)
    const before = favorited
    setFavorited(!before)
    try {
      setFavorited(before ? await api.unfavorite(docId) : await api.favorite(docId))
    } catch {
      setFavorited(before)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5 rounded-[var(--radius-sh)] border border-line bg-surface p-0.5">
        <VoteButton dir="up" active={myVote === 1} onClick={() => void vote(1)} disabled={busy} />
        <span className="min-w-8 text-center font-mono text-xs font-medium text-ink" aria-live="polite">
          {votes}
        </span>
        <VoteButton dir="down" active={myVote === -1} onClick={() => void vote(-1)} disabled={busy} />
      </div>

      <Button
        size="sm"
        variant={favorited ? 'primary' : 'secondary'}
        onClick={() => void toggleFavorite()}
        disabled={busy}
        aria-pressed={favorited}
      >
        <svg viewBox="0 0 24 24" className="size-3.5" fill={favorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8">
          <path strokeLinejoin="round" d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8L3.6 9.7l5.8-.8z" />
        </svg>
        {favorited ? 'Saved' : 'Save'}
      </Button>
    </div>
  )
}

function VoteButton({
  dir,
  active,
  ...props
}: { dir: 'up' | 'down'; active: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      aria-label={dir === 'up' ? 'Upvote' : 'Downvote'}
      aria-pressed={active}
      className={cx(
        'grid size-7 place-items-center rounded transition-colors disabled:opacity-50',
        active ? 'bg-accent-soft text-accent-text' : 'text-subtle hover:bg-sunken hover:text-ink',
      )}
    >
      <svg viewBox="0 0 24 24" className={cx('size-4', dir === 'down' && 'rotate-180')} fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-6 6m6-6l6 6" />
      </svg>
    </button>
  )
}
